import { SplitContext, SplitFactory, useTreatments } from '@splitsoftware/splitio-react'
import React from 'react'
import { useContext, useEffect, useState } from 'react'
import { PageLoading } from '../components/PageLoading'
import { config } from '../modules/app/config'
import { useCurrentUser } from './useCurrentUser'
import { logger } from '../modules/app/logger'

export type BASIC_TREATMENTS = 'on' | 'off'

export enum Feature {
    BillPay = 'BILL_PAY',
    KYC = 'KYC',
    Referrals = 'REFERRALS',
    OffRamp = 'OFFRAMP',
    Pricing = 'PRICING',
    SmartPay = 'SMART_PAY',
    SpritzCard = 'SPRITZ_CARD',
}

const FeatureFallback: Record<Feature, string> = {
    [Feature.BillPay]: 'on',
    [Feature.KYC]: 'on',
    [Feature.OffRamp]: 'on',
    [Feature.Pricing]: 'on',
    [Feature.Referrals]: 'on',
    [Feature.SmartPay]: 'off',
    [Feature.SpritzCard]: 'off',
}

const useGetUserAttributes = () => {
    const { me } = useCurrentUser()
    return {
        attributes: {
            user_id: me.id,
            email: me.email,
            phone: me?.phone,
            createdAt: new Date(me.createdAt),
            appDomain: me.appDomain,
        },
    }
}

interface IFeatureFlagContext {
    enabled: boolean
    ready: boolean
}

export const FeatureFlagContext = React.createContext<IFeatureFlagContext>(
    {} as IFeatureFlagContext
)

export const useFeatureFlagContext = () => {
    return React.useContext(FeatureFlagContext)
}

const FeatureFlagEnabledProvider: React.FC = ({ children }) => {
    const [handler, setHandler] = useState<NodeJS.Timeout | null>(null)
    const [enabled, setEnabled] = useState(true)
    const [ready, setReady] = useState(false)
    const { isReady: splitIsReady } = useContext(SplitContext)

    useEffect(() => {
        if (splitIsReady) {
            setReady(true)
            if (handler) clearTimeout(handler)
        }
    }, [splitIsReady, handler])

    useEffect(() => {
        setHandler(
            setTimeout(() => {
                setEnabled(false)
            }, 5000)
        )

        return () => {
            if (handler) clearTimeout(handler)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const value = {
        enabled,
        ready,
    }

    const isLoading = enabled && !ready

    if (isLoading) return <PageLoading />

    return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>
}

export const FeatureFlagProvider = ({ children }: { children: JSX.Element }) => {
    const { me } = useCurrentUser()
    return (
        <SplitFactory config={getSplitConfig(me.id)}>
            <FeatureFlagEnabledProvider>{children}</FeatureFlagEnabledProvider>
        </SplitFactory>
    )
}

export function getSplitConfig(userId: string) {
    return {
        core: {
            authorizationKey: config.splitApiToken,
            trafficType: 'user',
            key: userId,
        },
        userConsent: 'DENIED',
    }
}

export function useSplit<T, C>(feature: Feature): { treatment: T; config: null | C } {
    const { attributes } = useGetUserAttributes()
    const result = useTreatments([feature], attributes)[feature]
    const treatment = result.treatment as unknown as T
    let config: C | null = null

    try {
        if (result.config) {
            config = JSON.parse(result.config)
        }
    } catch (e) {
        logger.error(`Error decoding split config`)
    }
    return {
        treatment,
        config,
    }
}

export const useFeatureFlag = (feature: Feature): boolean => {
    const { enabled, ready } = useFeatureFlagContext()
    const isFeatureFlagOn = useSplit<BASIC_TREATMENTS, any>(feature).treatment === 'on'
    if (!enabled || !ready) return FeatureFallback[feature] === 'on'
    return isFeatureFlagOn
}
